import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import DealPanel from './DealPanel';
import {
  STAGE_META, COLUMN_ORDER, STAGES, PRIORITY_META, CATEGORIES, CLASSIFICATIONS, CLUB_OPTIONS, OPEN_STAGES,
  Pill, FollowUpPill, money, shortDate,
} from './esMeta';

// Events & Sponsorship sales (BK + Gene, 10/8/26). COMS owns this pipeline (docs/COMS_Events_Sponsorship_Build_Spec.md);
// Rounds will read results through the outcomes feed. Three views: Sponsorships, Events (each as a board or a sortable
// list, like Rounds' tracker) and Contacts (every person, with their own next follow-up).

const TABS = [
  { key: 'sponsorship', label: '🤝 Sponsorships' },
  { key: 'event', label: '📅 Events' },
  { key: 'contacts', label: '👥 Contacts' },
];

function ClubFilter({ club, setClub }) {
  return (
    <select className="field-input" style={{ width: 170, padding: '4px 8px', fontSize: 12 }} value={club} onChange={e => setClub(e.target.value)}>
      <option value="ALL">All clubs</option>
      {CLUB_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

function DealCard({ deal, onOpen }) {
  return (
    <div onClick={() => onOpen(deal)} style={{ background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7, padding: '8px 9px', marginBottom: 6, cursor: 'pointer' }}>
      <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.account_name}</div>
      {deal.name !== deal.account_name && <div style={{ fontSize: 10.5, color: '#aaa', marginBottom: 2 }}>{deal.name}</div>}
      {deal.contact_name && (
        <div style={{ fontSize: 10.5, color: '#aaa' }}>
          {deal.contact_name}{deal.contact_count > 1 ? ` +${deal.contact_count - 1}` : ''}
          {deal.contact_phone && <div><a href={`tel:${deal.contact_phone}`} onClick={e => e.stopPropagation()} style={{ color: '#888' }}>{deal.contact_phone}</a></div>}
          {deal.contact_email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><a href={`mailto:${deal.contact_email}`} onClick={e => e.stopPropagation()} style={{ color: '#888' }}>{deal.contact_email}</a></div>}
        </div>
      )}
      {!deal.contact_name && <Pill color="#6b7280">No contact person yet</Pill>}
      <div style={{ marginTop: 4 }}>
        {deal.priority && <Pill color={PRIORITY_META[deal.priority].color}>{PRIORITY_META[deal.priority].label}</Pill>}
        <Pill color="#6b7280">{deal.location_id || 'Multi'}</Pill>
        {deal.value !== null && <Pill color="#22c55e">{money(deal.value)}</Pill>}
        {deal.deal_type === 'event' && deal.event_date && <Pill color="#c084fc">Event {shortDate(String(deal.event_date).slice(0, 10))}</Pill>}
        <FollowUpPill date={deal.follow_up_date} overdue={deal.follow_up_overdue}
          missing={OPEN_STAGES.includes(deal.stage) && deal.stage !== 'new_lead' && !deal.follow_up_date && !deal.no_follow_up_reason && deal.attempts > 0} />
      </div>
      <div style={{ fontSize: 9.5, color: '#777', marginTop: 2 }}>
        {deal.last_contact_at ? `Last contact ${shortDate(deal.last_contact_at)} · ${deal.attempts} attempt${deal.attempts === 1 ? '' : 's'}` : 'Not contacted'}
        {deal.rep_name && ` · ${deal.rep_name}`}
      </div>
    </div>
  );
}

function Board({ deals, onOpen }) {
  const byCol = useMemo(() => {
    const m = Object.fromEntries(COLUMN_ORDER.map(k => [k, []]));
    for (const d of deals) (m[d.column] || m[d.stage]).push(d);
    // Follow-ups: soonest first. Everything else: high priority first, then most recently touched.
    const pr = { high: 0, medium: 1, low: 2 };
    for (const k of COLUMN_ORDER) {
      m[k].sort((a, b) => k === 'follow_up'
        ? a.follow_up_date.localeCompare(b.follow_up_date)
        : (pr[a.priority] ?? 3) - (pr[b.priority] ?? 3) || new Date(b.updated_at) - new Date(a.updated_at));
    }
    return m;
  }, [deals]);

  return (
    <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
      {COLUMN_ORDER.map((k, i) => {
        const cards = byCol[k];
        const total = cards.reduce((s, d) => s + (d.value || 0), 0);
        return (
          <div key={k} style={{ flex: '0 0 190px', width: 190 }}>
            <div style={{ background: '#1c1c1c', borderRadius: 7, padding: '5px 7px', marginBottom: 6, borderTop: `2px solid ${STAGE_META[k].color}` }}>
              <span style={{ float: 'right', fontFamily: 'monospace', fontSize: 10, background: '#0a0a0a', color: '#a8a496', padding: '1px 6px', borderRadius: 20 }}>{cards.length}</span>
              <div style={{ fontWeight: 700, fontSize: 10.5, color: '#a8a496' }}>{i + 1} · {STAGE_META[k].label}</div>
              <div style={{ fontSize: 9, color: '#888', marginTop: 3, lineHeight: 1.35 }}>{STAGE_META[k].desc}</div>
              {total > 0 && <div style={{ fontSize: 9.5, color: '#22c55e', marginTop: 3 }}>{money(total)}</div>}
            </div>
            {cards.length === 0 && (
              <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>Nothing here</div>
            )}
            {cards.map(d => <DealCard key={d.deal_id} deal={d} onOpen={onOpen} />)}
          </div>
        );
      })}
    </div>
  );
}

const SORTS = {
  account: d => (d.account_name || '').toLowerCase(),
  club: d => d.location_id || 'zzz',
  category: d => d.category || 'zzz',
  priority: d => ({ high: 0, medium: 1, low: 2 }[d.priority] ?? 3),
  stage: d => COLUMN_ORDER.indexOf(d.column),
  value: d => -(d.value || 0),
  follow_up: d => d.follow_up_date || '9999',
  last: d => -(d.last_contact_at ? new Date(d.last_contact_at).getTime() : 0),
};

function ListView({ deals, onOpen }) {
  const [sort, setSort] = useState('priority');
  const rows = useMemo(() => [...deals].sort((a, b) => {
    const x = SORTS[sort](a), y = SORTS[sort](b);
    return x < y ? -1 : x > y ? 1 : (a.account_name || '').localeCompare(b.account_name || '');
  }), [deals, sort]);
  const th = (key, text) => (
    <th onClick={() => setSort(key)} style={{ cursor: 'pointer', textAlign: 'left', padding: '6px 8px', fontSize: 10.5, color: sort === key ? '#fff' : '#888', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {text}{sort === key ? ' ▾' : ''}
    </th>
  );
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead><tr style={{ borderBottom: '1px solid #2a2a2a' }}>
          {th('account', 'Account')}{th('club', 'Club')}{th('category', 'Category')}{th('priority', 'Priority')}
          {th('stage', 'Stage')}{th('value', 'Value')}<th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10.5, color: '#888', textTransform: 'uppercase' }}>Contact</th>
          {th('last', 'Last contact')}{th('follow_up', 'Follow up')}
        </tr></thead>
        <tbody>
          {rows.map(d => (
            <tr key={d.deal_id} onClick={() => onOpen(d)} style={{ borderBottom: '1px solid #1f1f1f', cursor: 'pointer' }}>
              <td style={{ padding: '6px 8px' }}>
                <div style={{ fontWeight: 600 }}>{d.account_name}</div>
                {d.name !== d.account_name && <div style={{ fontSize: 10.5, color: '#888' }}>{d.name}</div>}
              </td>
              <td style={{ padding: '6px 8px' }}>{d.location_id || 'Multi'}</td>
              <td style={{ padding: '6px 8px', color: '#aaa' }}>{CATEGORIES[d.category] || ''}</td>
              <td style={{ padding: '6px 8px' }}>{d.priority && <Pill color={PRIORITY_META[d.priority].color}>{PRIORITY_META[d.priority].label}</Pill>}</td>
              <td style={{ padding: '6px 8px' }}><Pill color={STAGE_META[d.stage].color}>{STAGE_META[d.stage].label}</Pill></td>
              <td style={{ padding: '6px 8px' }}>{money(d.value)}</td>
              <td style={{ padding: '6px 8px', color: '#aaa' }}>{d.contact_name || ''}</td>
              <td style={{ padding: '6px 8px', color: '#aaa' }}>{d.last_contact_at ? shortDate(d.last_contact_at) : '—'}</td>
              <td style={{ padding: '6px 8px' }}><FollowUpPill date={d.follow_up_date} overdue={d.follow_up_overdue} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <div className="empty-state"><p>Nothing here yet.</p></div>}
    </div>
  );
}

function NewDealModal({ type, onClose, onCreated }) {
  const { get, post } = useApi();
  const [mode, setMode] = useState('new');
  const [search, setSearch] = useState('');
  const [matches, setMatches] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [f, setF] = useState({
    account_name: '', category: '', location_id: '', contact_name: '', contact_title: '', contact_email: '', contact_phone: '',
    name: '', priority: 'medium', value: '', classification: 'new', event_date: '', stage: 'new_lead',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (mode !== 'existing') return;
    const t = setTimeout(() => get(`/es/accounts?q=${encodeURIComponent(search)}`).then(r => setMatches(r.accounts)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    setError('');
    if (mode === 'new' && !f.account_name.trim()) return setError('Company name required.');
    if (mode === 'existing' && !accountId) return setError('Pick an account.');
    setSaving(true);
    try {
      const r = await post('/es/deals', {
        deal_type: type,
        account_id: mode === 'existing' ? accountId : null,
        account: mode === 'new' ? { name: f.account_name, category: f.category || null } : null,
        contact: { name: f.contact_name, title: f.contact_title, email: f.contact_email, phone: f.contact_phone },
        name: f.name, location_id: f.location_id || null, priority: f.priority || null, value: f.value,
        classification: f.classification, event_date: f.event_date || null, stage: f.stage,
      });
      onCreated(r.deal);
    } catch (e) { setError(e.message); setSaving(false); }
  }

  const lbl = { display: 'block', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', margin: '8px 0 3px' };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, width: '100%' }}>
        <div className="modal-title">New {type === 'event' ? 'event' : 'sponsorship'} lead</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button className={`btn btn-sm ${mode === 'new' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('new')}>New company</button>
          <button className={`btn btn-sm ${mode === 'existing' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('existing')}>Existing account</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
          {mode === 'new' ? (
            <>
              <div><span style={lbl}>Company</span><input className="field-input" value={f.account_name} onChange={e => set('account_name', e.target.value)} /></div>
              <div><span style={lbl}>Category</span>
                <select className="field-input" value={f.category} onChange={e => set('category', e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </>
          ) : (
            <div style={{ gridColumn: 'span 2' }}>
              <span style={lbl}>Find account</span>
              <input className="field-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Type a company name" />
              <select className="field-input" size={Math.min(6, Math.max(2, matches.length))} style={{ marginTop: 4 }} value={accountId} onChange={e => setAccountId(e.target.value)}>
                {matches.map(a => <option key={a.account_id} value={a.account_id}>{a.name}{a.location_id ? ` (${a.location_id})` : ''}</option>)}
              </select>
            </div>
          )}
          <div><span style={lbl}>Contact name</span><input className="field-input" value={f.contact_name} onChange={e => set('contact_name', e.target.value)} /></div>
          <div><span style={lbl}>Title</span><input className="field-input" value={f.contact_title} onChange={e => set('contact_title', e.target.value)} /></div>
          <div><span style={lbl}>Email</span><input className="field-input" value={f.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
          <div><span style={lbl}>Phone</span><input className="field-input" value={f.contact_phone} onChange={e => set('contact_phone', e.target.value)} /></div>
          <div style={{ gridColumn: 'span 2' }}><span style={lbl}>{type === 'event' ? 'Event name (optional)' : 'Deal name (optional)'}</span>
            <input className="field-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="Defaults to the company name" /></div>
          <div><span style={lbl}>Club</span>
            <select className="field-input" value={f.location_id} onChange={e => set('location_id', e.target.value)}>
              <option value="">Multi-club / not set</option>
              {CLUB_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><span style={lbl}>Priority</span>
            <select className="field-input" value={f.priority} onChange={e => set('priority', e.target.value)}>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div><span style={lbl}>{type === 'event' ? 'Price ($)' : 'Value per year ($)'}</span><input className="field-input" value={f.value} onChange={e => set('value', e.target.value)} /></div>
          {type === 'event' ? (
            <div><span style={lbl}>Event date</span><input type="date" className="field-input" value={f.event_date} onChange={e => set('event_date', e.target.value)} /></div>
          ) : (
            <div><span style={lbl}>Type</span>
              <select className="field-input" value={f.classification} onChange={e => set('classification', e.target.value)}>
                {Object.entries(CLASSIFICATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
          <div><span style={lbl}>Stage</span>
            <select className="field-input" value={f.stage} onChange={e => set('stage', e.target.value)}>
              {STAGES.filter(s => !s.startsWith('closed_')).map(s => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
            </select>
          </div>
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{error}</div>}
        <div className="modal-footer" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

// Every person across every account, with their own next follow-up (BK, 10/8/26). Anyone on an open deal without a
// follow-up or a recorded reason is flagged.
function ContactsList({ club, onOpenDeal }) {
  const { get } = useApi();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [show, setShow] = useState('all');

  useEffect(() => {
    setRows(null);
    get(`/es/contacts?club=${club}`).then(r => setRows(r.contacts)).catch(() => setRows([]));
  }, [club]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows) return <div className="empty-state"><div className="spinner" /></div>;
  const needs = rows.filter(r => r.needs_follow_up).length;
  const overdue = rows.filter(r => r.follow_up_overdue).length;
  const filtered = rows
    .filter(r => show === 'all' || (show === 'needs' && r.needs_follow_up) || (show === 'overdue' && r.follow_up_overdue) || (show === 'scheduled' && r.follow_up_date))
    .filter(r => !q || `${r.name} ${r.account_name} ${r.email || ''} ${r.phone || ''} ${r.title || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <input className="field-input" style={{ width: 240, padding: '4px 8px', fontSize: 12 }} placeholder="Search name, company, email, phone" value={q} onChange={e => setQ(e.target.value)} />
        {[['all', `All (${rows.length})`], ['needs', `No follow-up set (${needs})`], ['overdue', `Overdue (${overdue})`], ['scheduled', 'Scheduled']].map(([k, t]) => (
          <button key={k} className={`btn btn-sm ${show === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setShow(k)}>{t}</button>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ borderBottom: '1px solid #2a2a2a', color: '#888', fontSize: 10.5, textTransform: 'uppercase', textAlign: 'left' }}>
            <th style={{ padding: '6px 8px' }}>Name</th><th style={{ padding: '6px 8px' }}>Company</th><th style={{ padding: '6px 8px' }}>Phone / email</th>
            <th style={{ padding: '6px 8px' }}>Deals</th><th style={{ padding: '6px 8px' }}>Last contact</th><th style={{ padding: '6px 8px' }}>Next follow-up</th>
          </tr></thead>
          <tbody>
            {filtered.map(r => {
              const openDeal = r.deals.find(d => OPEN_STAGES.includes(d.stage)) || r.deals[0];
              return (
                <tr key={r.contact_id} onClick={() => openDeal && onOpenDeal(openDeal)} style={{ borderBottom: '1px solid #1f1f1f', cursor: openDeal ? 'pointer' : 'default' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ fontWeight: 600 }}>{r.name}{r.is_primary && <span style={{ color: '#888', fontWeight: 400 }}> · primary</span>}</div>
                    {r.title && <div style={{ fontSize: 10.5, color: '#888' }}>{r.title}</div>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{r.account_name}<div style={{ fontSize: 10.5, color: '#888' }}>{r.location_id || 'Multi-club'}{r.category ? ` · ${CATEGORIES[r.category]}` : ''}</div></td>
                  <td style={{ padding: '6px 8px', fontSize: 11.5 }}>
                    {r.phone && <div><a href={`tel:${r.phone}`} onClick={e => e.stopPropagation()} style={{ color: '#aaa' }}>{r.phone}</a></div>}
                    {r.email && <div><a href={`mailto:${r.email}`} onClick={e => e.stopPropagation()} style={{ color: '#aaa' }}>{r.email}</a></div>}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    {r.deals.map(d => <div key={d.deal_id}><Pill color={STAGE_META[d.stage].color}>{d.deal_type === 'event' ? 'Event' : 'Spons.'} · {STAGE_META[d.stage].label}</Pill></div>)}
                  </td>
                  <td style={{ padding: '6px 8px', color: '#aaa' }}>{r.last_contact_at ? shortDate(r.last_contact_at) : '—'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <FollowUpPill date={r.on_open_deal ? r.follow_up_date : null} overdue={r.follow_up_overdue} missing={r.needs_follow_up} />
                    {r.on_open_deal && r.follow_up_note && <div style={{ fontSize: 10.5, color: '#888' }}>{r.follow_up_note}</div>}
                    {!r.follow_up_date && r.no_follow_up_reason && <div style={{ fontSize: 10.5, color: '#888' }}>None: {r.no_follow_up_reason}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state"><p>No contacts.</p></div>}
      </div>
    </div>
  );
}

export default function EventsSponsorshipModule() {
  const { get } = useApi();
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('es_tab') || 'sponsorship'; } catch { return 'sponsorship'; } });
  const [view, setView] = useState(() => { try { return localStorage.getItem('es_view') || 'board'; } catch { return 'board'; } });
  const [club, setClub] = useState('ALL');
  const [deals, setDeals] = useState(null);
  const [error, setError] = useState('');
  const [openDealId, setOpenDealId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [contactsKey, setContactsKey] = useState(0);

  useEffect(() => { try { localStorage.setItem('es_tab', tab); localStorage.setItem('es_view', view); } catch { /* ignore */ } }, [tab, view]);

  const load = useCallback(() => {
    if (tab === 'contacts') return;
    setError('');
    get(`/es/board?type=${tab}&club=${club}`).then(r => setDeals(r.deals)).catch(e => setError(e.message));
  }, [tab, club]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDeals(null); load(); }, [load]);

  const openCount = (deals || []).filter(d => OPEN_STAGES.includes(d.stage)).length;
  const pipelineValue = (deals || []).filter(d => OPEN_STAGES.includes(d.stage)).reduce((s, d) => s + (d.value || 0), 0);
  const overdue = (deals || []).filter(d => d.follow_up_overdue).length;

  return (
    <div className="module-page kanban-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>🤝</span>
          <h1 className="module-title">Events &amp; Sponsorship Sales</h1>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <ClubFilter club={club} setClub={setClub} />
          {tab !== 'contacts' && (
            <>
              <button className={`btn btn-sm ${view === 'board' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('board')}>Board</button>
              <button className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('list')}>List</button>
              <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>+ New {tab === 'event' ? 'event' : 'sponsor'} lead</button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {TABS.map(t => (
          <button key={t.key} className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
        {tab !== 'contacts' && deals && (
          <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>
            {openCount} open · {money(pipelineValue) || '$0'} in pipeline{overdue ? <span style={{ color: '#ef4444' }}> · {overdue} follow-up{overdue === 1 ? '' : 's'} overdue</span> : ''}
          </span>
        )}
      </div>

      {error && <div className="empty-state"><p style={{ color: '#ef4444' }}>{error}</p></div>}
      {tab === 'contacts' ? (
        <ContactsList key={contactsKey} club={club} onOpenDeal={d => setOpenDealId(d.deal_id)} />
      ) : !deals ? (
        !error && <div className="empty-state"><div className="spinner" /></div>
      ) : view === 'board' ? (
        <Board deals={deals} onOpen={d => setOpenDealId(d.deal_id)} />
      ) : (
        <ListView deals={deals} onOpen={d => setOpenDealId(d.deal_id)} />
      )}

      {openDealId && (
        <DealPanel dealId={openDealId} onClose={() => setOpenDealId(null)} onChanged={() => { load(); setContactsKey(k => k + 1); }} />
      )}
      {creating && (
        <NewDealModal type={tab === 'event' ? 'event' : 'sponsorship'} onClose={() => setCreating(false)}
          onCreated={deal => { setCreating(false); load(); setOpenDealId(deal.deal_id); }} />
      )}
    </div>
  );
}
